import { Stack, Typography } from '@mui/material'
import { twinsUpdate } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { TwinRole, TwinUpdate } from 'api/model'
import { COLORS } from 'app-constants'
import { useActiveOrganization } from 'hooks/use-organization'
import { useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { myTwinAtom } from 'state/my-twin'
import { twinsAtom } from 'state/twins'

const TwinRoleSelector = ({
  isDarkBackdrop,
}: {
  isDarkBackdrop: boolean // used to conditionally style label text color, sometimes this component is on a light / dark backdrop
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const myTwin = useRecoilValue(myTwinAtom)
  const setTwins = useSetRecoilState(twinsAtom)
  const setMyTwin = useSetRecoilState(myTwinAtom)

  const [twinRole, setTwinRole] = useState<TwinRole>(myTwin?.role ?? TwinRole.user_twin)

  const onTwinRoleChange = async (role: TwinRole) => {
    if (!myTwin || !activeOrganization) {
      return
    }

    try {
      const update: TwinUpdate = {
        name: myTwin.name,
        role: role,
      }
      const result = await twinsUpdate(activeOrganization, myTwin.id, update)

      // update twins in recoil
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === myTwin.id) {
            return result.data
          }
          return t
        })
      })

      // update my twin atom in recoil
      setMyTwin((myTwin) => {
        if (!myTwin) {
          return null
        }
        return result.data
      })

      setTwinRole(role)

      setToast({ title: 'Successfully updated twin role', severity: 'success' })
    } catch (err) {
      setToast({ title: 'Failed to upload twin role', severity: 'error' })
    }
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing="4px"
      height="40px"
      borderRadius="10px"
      p="4px"
      mb={4}
      sx={{
        background: COLORS.GRAY_7,
      }}>
      <Stack
        textAlign="center"
        p="6px 17px"
        width="98px"
        borderRadius="8px"
        onClick={() => onTwinRoleChange(TwinRole.user_twin)}
        sx={{
          background: twinRole === TwinRole.user_twin ? COLORS.PURE_WHITE : 'transparent',
          boxShadow: '1px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          cursor: 'pointer',
        }}>
        <Typography
          variant="pXS"
          sx={{
            color: twinRole === TwinRole.user_twin ? COLORS.GRAY_0 : COLORS.GRAY_3,
          }}>
          User twin
        </Typography>
      </Stack>

      <Stack
        p="6px 6px"
        textAlign="center"
        width="98px"
        borderRadius="8px"
        onClick={() => onTwinRoleChange(TwinRole.support_twin)}
        sx={{
          background: twinRole === TwinRole.support_twin ? COLORS.PURE_WHITE : 'transparent',
          boxShadow: '1px 1px 2px 0px rgba(0, 0, 0, 0.08)',
          cursor: 'pointer',
        }}>
        <Typography
          variant="pXS"
          sx={{
            color: twinRole === TwinRole.support_twin ? COLORS.GRAY_0 : COLORS.GRAY_3,
          }}>
          Support twin
        </Typography>
      </Stack>
    </Stack>
  )
}

export default TwinRoleSelector
